import React, { useEffect, useState } from 'react';
import { X, Printer, RefreshCw } from 'lucide-react';
import { InvoicePreview } from './InvoicePreview';
import { getInvoicePreview } from '@/services/api/clientBillingAPI';
import './InvoicePrintStyles.css';

interface InvoicePreviewModalProps {
  invoiceId: string | number;
  /** Scope to a client org when an admin views a client's invoice; omit for self-service. */
  cboId?: number;
  onClose: () => void;
}

/**
 * Client-facing invoice view. Renders the SAME formatted invoice the operator
 * invoice page shows (InvoicePreview), fed by the server-side preview payload
 * that merges the issuer's (Trust Care) template branding. Print produces the
 * PDF from this exact rendering — no separate sparse regeneration.
 */
export function InvoicePreviewModal({ invoiceId, cboId, onClose }: InvoicePreviewModalProps) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await getInvoicePreview(invoiceId, cboId);
        if (!cancelled) setData(d);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load invoice');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId, cboId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 print:bg-white print:p-0">
      <div className="my-8 w-full max-w-3xl print:my-0 print:max-w-none">
        {/* Toolbar — hidden when printing */}
        <div className="flex items-center justify-between mb-3 print:hidden">
          <h3 className="text-base font-semibold text-white">Invoice</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              disabled={!data}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white text-gray-800 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 text-white hover:bg-white/20"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-white p-12 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading invoice…
          </div>
        ) : error ? (
          <div className="rounded-lg bg-white p-6 text-sm text-red-600">{error}</div>
        ) : data ? (
          <div id="invoice-preview-printable">
            <InvoicePreview data={data} printMode />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default InvoicePreviewModal;
